import { merge } from 'lodash';
import { DataPrimitiveFormatter } from '../Formatter';
import { DataType } from '../DataPrimitive';
import { DataPoint } from '../DataPoint';
import { DataSeries } from '../DataSeries';
import { DataFrame } from '../DataFrame';
import EncodingExecutor from '../EncodingExecutor';

interface ObjectConfig {
    rename: { [key: string]: string };
}

/**
 * Formatter which converts a DataPrimitive into an array of objects. This is typically used in conjunction with UDF inputs.
 *
 * ```js
 * // UDF inputs
 * {
 *     "inputs": {
 *         "input1": {
 *             "context": {
 *                  "staticOptions": [["All Users"], ["*"]],
 *                  "field1": "> primary | seriesByName(\"users\") | renameSeries(\"label\")",
 *                  "field2": "> primary | seriesByName(\"ids\") | renameSeries(\"value\")"",
 *              },
 *              "type": "input.multiselect",
 *              "options": {
 *                  "items": "> frame(field1, field2) | prepend(staticOptions) | objects()"
 *                      // returns [
 *                      //     { label: 'All users', value: '*' },
 *                      //     { label: 'jane doe', value: 'jdoe' },
 *                      //     { label: 'joe schmo', value: 'jschmo' },
 *                      //     { label: 'jack schmidt', value: 'jschmidt' },
 *                      // ]
 *                  ]
 *              },
 *              "dataSources": {
 *                  "primary": {
 *                      "data": {
 *                          "fields": [{ "name": 'users' } , { "name": "ids" } ],
 *                          "columns": [[ "jane doe", "joe schmo", "jack schmidt"], ["jdoe", "jschmo", "jschmidt"]]
 *                      }
 *                  }
 *             },
 *         }
 *     }
 * }
 * ```
 */
export class Objects implements DataPrimitiveFormatter<DataType, DataType> {
    /**
     * Helper to attach enumerable isSearchResult property to an object.
     * Enumerable so it survives worker serialization via structured clone / postMessage.
     * Writable to avoid issues with Object.assign and other cloning operations.
     */
    static attachMeta(target: Record<string, unknown>, isSearchResult: boolean) {
        Object.defineProperty(target, 'isSearchResult', {
            value: isSearchResult,
            enumerable: true,
            writable: false,
            configurable: true,
        });
    }

    private readonly config: ObjectConfig;

    constructor(objectConfig?: ObjectConfig) {
        this.config = EncodingExecutor.rawTree(objectConfig);
    }

    format(dp: DataPoint<DataType> | DataSeries<DataType> | DataFrame<DataType>): Record<string, any> {
        if (DataFrame.isDataFrame(dp)) {
            // Merge series row-wise, propagate metadata
            const mergedObjectSeries = this.seriesToObjects(dp.series[0]);
            for (let i = 1; i < dp.series.length; i += 1) {
                const mergeMeIn = this.seriesToObjects(dp.series[i]);
                for (let j = 0; j < mergedObjectSeries.length; j += 1) {
                    const sourceObj = mergedObjectSeries[j] as any;
                    const mergeObj = mergeMeIn[j] as any;

                    // Check if each has the metadata descriptor (non-writable), not just any isSearchResult value
                    const sourceHasMetadata =
                        sourceObj != null &&
                        Object.getOwnPropertyDescriptor(sourceObj, 'isSearchResult')?.writable === false;
                    const mergeHasMetadata =
                        mergeObj != null &&
                        Object.getOwnPropertyDescriptor(mergeObj, 'isSearchResult')?.writable === false;
                    const bothAreSearchResults = sourceHasMetadata && mergeHasMetadata;

                    // Use empty object as target to avoid mutating sourceObj
                    mergedObjectSeries[j] = merge({}, sourceObj, mergeObj);

                    if (bothAreSearchResults) {
                        // Re-attach metadata descriptor
                        Objects.attachMeta(mergedObjectSeries[j], true);
                    } else if (sourceHasMetadata) {
                        // Remove metadata inherited from source; user data fields are safe
                        delete (mergedObjectSeries[j] as any).isSearchResult;
                    }
                }
            }
            return mergedObjectSeries;
        }
        if (DataSeries.isDataSeries(dp)) {
            return this.seriesToObjects(dp);
        }
        if (DataPoint.isDataPoint(dp)) {
            const obj: Record<string, unknown> = { [dp.field]: dp.getRawValue() };
            // Don't attach metadata if the field name itself is 'isSearchResult'
            if (dp.isSearchResult && dp.field !== 'isSearchResult') {
                Objects.attachMeta(obj, true);
            }
            return [obj];
        }
        throw new Error(`'objects' formatter only allowed on DataFrame, DataPoint, or DataSeries`);
    }

    private seriesToObjects(s: DataSeries): Record<string, unknown>[] {
        return s.points.map(pt => {
            const key = this.config?.rename?.[pt.field] ?? pt.field;
            const obj: Record<string, unknown> = { [key]: pt.getRawValue() };
            // Don't overwrite real data columns named 'isSearchResult' with metadata
            if (pt.isSearchResult && key !== 'isSearchResult') {
                Objects.attachMeta(obj, true);
            }
            return obj;
        });
    }
}
